import type { BaseMessage } from "@langchain/core/messages";
import type {
  AssembledToolCall,
  SubagentDiscoverySnapshot,
} from "@langchain/langgraph-sdk/stream";
import { useMessages, useToolCalls } from "@langchain/react";
import { Typography } from "@mui/material";
import { Bot } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MarkdownText } from "../../../ui/MarkdownText";
import { DO_NOT_RENDER_ID_PREFIX } from "../../ensure-tool-responses";
import type { GroupedToolCall } from "../../group-tool-messages";
import { matchPendingReviewsToCalls } from "../../match-pending-reviews";
import {
  PendingReviewPlacement,
  PendingToolReviewScope,
  usePendingToolReviews,
} from "../../PendingToolReviewScope";
import { type StreamContextType, useStreamContext } from "../../Stream";
import {
  type ChatMessage,
  type ChatToolCall,
  type ChatToolMessage,
  getChatMessageType,
} from "../../types";
import { mapToolResultToToolCall } from "../../utils";
import { getAIMessageRenderState } from "../AIMessage";
import { ThinkingBlock } from "../ThinkingBlock";
import { ToolCallBlock } from "../ToolCallBlock";
import { TOOL_CONFIGS, ToolCallName } from "../utils/tool-calls";
import { GenericToolCall } from "./GenericToolCall";
import { ToolCallFrame } from "./ToolCallFrame";

interface SubagentTaskToolProps {
  toolCall: {
    id?: string;
    name?: string;
  };
  args: Record<string, unknown>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
}

const isSerializedLangChainConstructor = (message: ChatMessage) =>
  (message as any).lc === 1 &&
  message.type === "constructor" &&
  Array.isArray((message as any).id) &&
  Object.prototype.hasOwnProperty.call(message, "kwargs");

const getVisibleSubagentMessages = (messages: BaseMessage[]) =>
  (messages as unknown as ChatMessage[])
    .filter(
      (message) =>
        !message.id?.startsWith(DO_NOT_RENDER_ID_PREFIX) &&
        !isSerializedLangChainConstructor(message)
    )
    .filter((message) => getChatMessageType(message) !== "human")
    .filter((message) => getChatMessageType(message) !== "tool");

const toolOutputToString = (output: unknown) => {
  if (typeof output === "string") {
    return output;
  }

  if (output && typeof output === "object" && "content" in output) {
    const content = (output as { content?: unknown }).content;
    if (typeof content === "string") {
      return content;
    }
  }

  try {
    return JSON.stringify(output ?? "", null, 2);
  } catch {
    return String(output ?? "");
  }
};

// The SDK reports LangGraph approval pauses as serialized tool errors and
// retains them while the resumed subagent streams its next messages.
const isApprovalInterrupt = (content: unknown) =>
  typeof content === "string" &&
  content.trimStart().startsWith("(Interrupt(value=");

const toChatToolCall = (toolCall: AssembledToolCall): ChatToolCall => ({
  type: "tool_call",
  id: toolCall.callId || toolCall.id,
  name: toolCall.name,
  args: toolCall.args ?? toolCall.input,
});

const toChatToolMessage = (
  toolCall: AssembledToolCall
): ChatToolMessage | undefined => {
  const toolCallId = toolCall.callId || toolCall.id;
  if (!toolCallId) {
    return undefined;
  }

  if (toolCall.status === "error") {
    return {
      type: "tool",
      role: "tool",
      status: "error",
      tool_call_id: toolCallId,
      name: toolCall.name,
      content: toolCall.error || "Tool failed.",
    };
  }

  if (toolCall.status !== "finished") {
    return undefined;
  }

  return {
    type: "tool",
    role: "tool",
    status: "success",
    tool_call_id: toolCallId,
    name: toolCall.name,
    content: toolOutputToString(toolCall.output),
  };
};

const SubagentAIMessage = ({
  message,
  isLoading,
  isLastMessage,
  toolResultsMappedToToolCall,
  reviewByCall,
  decisions,
}: {
  message: ChatMessage;
  isLoading: boolean;
  isLastMessage: boolean;
  toolResultsMappedToToolCall?: ReadonlyMap<string, ChatToolMessage>;
  reviewByCall: ReadonlyMap<string, string>;
  decisions: ReadonlyMap<string, "approve" | "reject" | "edit">;
}) => {
  const {
    thinkingMessages,
    textContent,
    toolCallBlocks,
    hasToolCalls,
    hasThinkingMessages,
    everyToolCallHidden,
  } = getAIMessageRenderState(message, toolResultsMappedToToolCall);

  if (textContent.length === 0 && !hasToolCalls && !hasThinkingMessages) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {thinkingMessages.length > 0 && (
        <ThinkingBlock
          thinkingMessages={thinkingMessages}
          defaultExpanded={isLoading && isLastMessage}
        />
      )}

      {textContent.length > 0 && <MarkdownText>{textContent}</MarkdownText>}

      {hasToolCalls && !everyToolCallHidden && (
        <div className="flex flex-col">
          {toolCallBlocks.map((nestedToolCall, idx) => {
            const nestedToolCallId =
              "id" in nestedToolCall
                ? (nestedToolCall.id as string | undefined)
                : undefined;
            const nestedToolResult = nestedToolCallId
              ? toolResultsMappedToToolCall?.get(nestedToolCallId)
              : undefined;

            const reviewKey = nestedToolCallId
              ? reviewByCall.get(nestedToolCallId)
              : undefined;
            return reviewKey ? (
              <PendingReviewPlacement
                key={nestedToolCallId ?? `subagent-tool-${idx}`}
                reviewKey={reviewKey}
              />
            ) : (
              <ToolCallBlock
                key={nestedToolCallId ?? `subagent-tool-${idx}`}
                toolCall={nestedToolCall}
                toolResult={nestedToolResult}
                isLoading={isLoading && isLastMessage && !nestedToolResult}
                compact
                decision={
                  nestedToolCallId ? decisions.get(nestedToolCallId) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const DiscoveredSubagentTaskTool = ({
  subagent,
  args,
  toolResult,
  isLoading = false,
}: {
  subagent: SubagentDiscoverySnapshot;
  args: Record<string, unknown>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
}) => {
  const stream = useStreamContext();
  const pendingReviews = usePendingToolReviews();
  const isWaitingForApproval = pendingReviews.some(
    ({ namespace, decision }) =>
      !decision &&
      subagent.namespace.length > 0 &&
      subagent.namespace.every((part, index) => namespace[index] === part)
  );
  const messages = useMessages(stream, subagent);
  const toolCalls = useToolCalls(stream, subagent);
  const visibleMessages = useMemo(
    () => getVisibleSubagentMessages(messages),
    [messages]
  );
  const toolResultsMappedToToolCall = useMemo(
    () =>
      mapToolResultToToolCall(
        messages as unknown as StreamContextType["messages"]
      ),
    [messages]
  );
  const visibleToolCallIds = useMemo(
    () =>
      new Set(
        visibleMessages.flatMap((message) =>
          getChatMessageType(message) === "ai"
            ? getAIMessageRenderState(message).toolCallBlocks.flatMap(
                (toolCall) =>
                  typeof toolCall.id === "string" ? [toolCall.id] : []
              )
            : []
        )
      ),
    [visibleMessages]
  );
  const assembledToolCalls = useMemo(
    () =>
      toolCalls.map((nestedToolCall) => ({
        toolCall: toChatToolCall(nestedToolCall),
        toolResult: toChatToolMessage(nestedToolCall),
        isLoading: nestedToolCall.status === "running",
      })),
    [toolCalls]
  );
  const fallbackToolCalls = assembledToolCalls.filter(
    ({ toolCall }) => !toolCall.id || !visibleToolCallIds.has(toolCall.id)
  );
  const directChildCalls: GroupedToolCall[] = visibleMessages.flatMap(
    (message, messageIndex) =>
      getChatMessageType(message) === "ai"
        ? getAIMessageRenderState(message).toolCallBlocks.map(
            (toolCall, index) => ({
              id: toolCall.id ?? `${message.id ?? messageIndex}-${index}`,
              toolCall,
              messageId: message.id,
              messageIndex,
            })
          )
        : []
  );
  const fallbackCalls: GroupedToolCall[] = fallbackToolCalls.map(
    ({ toolCall }, index) => ({
      id: toolCall.id ?? `fallback-${index}`,
      toolCall,
      messageIndex: visibleMessages.length,
    })
  );
  const childReviews = pendingReviews.filter(
    (review) => review.subagentId === subagent.id
  );
  const reviewByCall = matchPendingReviewsToCalls(
    [...directChildCalls, ...fallbackCalls],
    childReviews,
    (id) =>
      Boolean(
        directChildCalls.some(
          (call) =>
            call.id === id &&
            toolResultsMappedToToolCall.get(call.messageId ?? "")?.has(id)
        )
      ) ||
      Boolean(
        fallbackToolCalls.some(
          ({ toolCall, toolResult }) => toolCall.id === id && toolResult
        )
      )
  );
  const [decidedCalls, setDecidedCalls] = useState<
    Record<string, "approve" | "reject" | "edit">
  >({});
  useEffect(() => {
    const additions: Record<string, "approve" | "reject" | "edit"> = {};
    reviewByCall.forEach((reviewKey, callId) => {
      const decision = childReviews.find(
        (review) => review.key === reviewKey
      )?.decision;
      if (decision) additions[callId] = decision;
    });
    if (Object.keys(additions).length)
      setDecidedCalls((current) =>
        Object.entries(additions).every(
          ([id, decision]) => current[id] === decision
        )
          ? current
          : { ...current, ...additions }
      );
  }, [reviewByCall, childReviews]);
  const decisions = new Map(Object.entries(decidedCalls));
  const pendingReviewByCall = new Map(
    [...reviewByCall].filter(
      ([, key]) => !childReviews.find((review) => review.key === key)?.decision
    )
  );
  const childCallCount = directChildCalls.length + fallbackCalls.length;

  const toolTitle =
    TOOL_CONFIGS[ToolCallName.TASK].title?.(
      args,
      ToolCallName.TASK,
      toolResult
    ) ?? "Delegating to subagent";
  const hasSubagentError =
    subagent.status === "error" && !isApprovalInterrupt(subagent.error);
  const hasToolError =
    toolResult?.status === "error" && !isApprovalInterrupt(toolResult.content);
  const hasError = hasSubagentError || hasToolError;
  const isSubagentLoading =
    !isWaitingForApproval && (isLoading || subagent.status === "running");
  const hasNestedMessages = visibleMessages.length > 0;
  const hasFallbackToolCalls = fallbackToolCalls.length > 0;
  const hasFallbackResult =
    !isWaitingForApproval &&
    !hasNestedMessages &&
    !hasFallbackToolCalls &&
    !hasError &&
    !isApprovalInterrupt(toolResult?.content) &&
    typeof toolResult?.content !== "undefined";
  const errorContent = hasSubagentError
    ? subagent.error
    : hasToolError
      ? toolResult?.content
      : undefined;
  const hasErrorContent = hasError && !!errorContent;
  const hasContent =
    isWaitingForApproval ||
    hasNestedMessages ||
    hasFallbackToolCalls ||
    hasFallbackResult ||
    hasErrorContent;

  return (
    <ToolCallFrame
      title={
        <span className="flex min-w-0 items-center gap-[10px]">
          <Bot
            aria-hidden="true"
            className="h-[16px] w-[16px] shrink-0 text-black/55"
          />
          <span className="truncate">
            {toolTitle.replace("Delegating to", "Delegated to")}
          </span>
        </span>
      }
      isLoading={isSubagentLoading}
      hasError={hasError}
      isExpandable={hasContent}
      expandedByDefault={TOOL_CONFIGS[ToolCallName.TASK].expandedByDefault}
      hasToolResult={hasContent || !!toolResult}
      hasContent={hasContent}
      keepContentMounted={isWaitingForApproval}
      compact
      actions={
        isWaitingForApproval ? (
          <span className="inline-flex h-[20px] items-center rounded-full border border-solid border-[#fed7aa] bg-[#fff7ed] px-2 text-xs text-[#b45309] whitespace-nowrap">
            Waiting on you
          </span>
        ) : childCallCount > 0 ? (
          <Typography
            component="span"
            className="text-xs whitespace-nowrap text-[#6b7280]"
          >
            {childCallCount} tools
          </Typography>
        ) : undefined
      }
    >
      <div className="flex flex-col">
        <PendingToolReviewScope subagentId={subagent.id}>
          {hasNestedMessages &&
            visibleMessages.map((message, index) => {
              const messageId = message.id;
              const isLastMessage =
                messageId === visibleMessages[visibleMessages.length - 1]?.id;

              if (getChatMessageType(message) !== "ai") {
                return null;
              }

              return (
                <SubagentAIMessage
                  key={messageId ?? `subagent-message-${index}`}
                  message={message}
                  isLoading={isSubagentLoading && isLastMessage}
                  isLastMessage={isLastMessage}
                  toolResultsMappedToToolCall={
                    messageId
                      ? toolResultsMappedToToolCall.get(messageId)
                      : undefined
                  }
                  reviewByCall={pendingReviewByCall}
                  decisions={decisions}
                />
              );
            })}

          {hasFallbackToolCalls &&
            fallbackToolCalls.map(
              ({ toolCall, toolResult, isLoading }, index) => {
                const reviewKey = toolCall.id
                  ? pendingReviewByCall.get(toolCall.id)
                  : undefined;
                return reviewKey ? (
                  <PendingReviewPlacement
                    key={toolCall.id ?? index}
                    reviewKey={reviewKey}
                  />
                ) : (
                  <ToolCallBlock
                    key={toolCall.id ?? `subagent-fallback-tool-${index}`}
                    toolCall={toolCall}
                    toolResult={toolResult}
                    isLoading={isLoading}
                    compact
                    decision={
                      toolCall.id ? decisions.get(toolCall.id) : undefined
                    }
                  />
                );
              }
            )}

          {hasFallbackResult && (
            <MarkdownText>
              {toolOutputToString(toolResult?.content)}
            </MarkdownText>
          )}

          {hasErrorContent && (
            <MarkdownText>{toolOutputToString(errorContent)}</MarkdownText>
          )}
        </PendingToolReviewScope>
      </div>
    </ToolCallFrame>
  );
};

export const SubagentTaskTool = ({
  toolCall,
  args,
  toolResult,
  isLoading = false,
}: SubagentTaskToolProps) => {
  const stream = useStreamContext();
  const subagent =
    typeof toolCall.id === "string" ? stream.subagents.get(toolCall.id) : null;

  if (!subagent) {
    return (
      <GenericToolCall
        toolName={ToolCallName.TASK}
        args={args}
        toolResult={toolResult}
        isLoading={isLoading}
      />
    );
  }

  return (
    <DiscoveredSubagentTaskTool
      subagent={subagent}
      args={args}
      toolResult={toolResult}
      isLoading={isLoading}
    />
  );
};
